import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = "/api";

// Create an axios instance that auto-attaches Bearer token
const api = axios.create({ baseURL: API, withCredentials: true });

function getStoredToken() {
  return localStorage.getItem("cc_access_token");
}
function getStoredRefresh() {
  return localStorage.getItem("cc_refresh_token");
}
function storeTokens(access, refresh) {
  if (access) localStorage.setItem("cc_access_token", access);
  if (refresh) localStorage.setItem("cc_refresh_token", refresh);
}
function clearTokens() {
  localStorage.removeItem("cc_access_token");
  localStorage.removeItem("cc_refresh_token");
}

// Interceptor: attach Bearer token to every request
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export { api };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshingRef = useRef(false);

  const checkAuth = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Try refresh
      if (err.response?.status === 401 && !refreshingRef.current) {
        refreshingRef.current = true;
        try {
          const refreshToken = getStoredRefresh();
          if (refreshToken) {
            const { data: refreshData } = await axios.post(`${API}/auth/refresh`, {}, {
              withCredentials: true,
              headers: { Authorization: `Bearer ${refreshToken}` }
            });
            if (refreshData.access_token) {
              storeTokens(refreshData.access_token, null);
              const { data } = await api.get("/auth/me");
              setUser(data);
              refreshingRef.current = false;
              setLoading(false);
              return;
            }
          }
        } catch {}
        refreshingRef.current = false;
      }
      setUser(null);
      clearTokens();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data.access_token) storeTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (name, email, password, social_media_url) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password, social_media_url });
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
    clearTokens();
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
      clearTokens();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
